/* eslint-disable max-lines-per-function */
import { Icon, ICONS, SvgIcon, useDocumentScrollBlock, useIsWindowBiggerThan } from '@bob/library'
import { CTA, ctaColorTheme } from '@websrc/App/Components/CTA'
import { MobileMenuAccordion } from '@websrc/App/Components/MobileMenuAccordion/MobileMenuAccordion'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { CTAType } from '@websrc/DataMappers/mapCTA'
import { mapServicebar } from '@websrc/DataMappers/mapServicebar'
import { mapTopbar } from '@websrc/DataMappers/mapTopbar'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { MainTopbarAuth } from '../Auth/MainTopbarAuth'
import { isButton, isCTA } from '../MainTopbar.helpers'
import { itemText, menuCTA } from '../MenuList/TopbarMenu.css'
import {
  MainTopbarRegionModal,
  MainTopbarRegionModalProps,
} from '../RegionModal/MainTopbarRegionModal'
import * as SC from './MainTopbarMobileModal.style'

export interface MainTopbarMobileModalProps {
  leftMenuItems: ReturnType<typeof mapTopbar>['left_menu_items'][0][]
  rightMenuItems: ReturnType<typeof mapTopbar>['menu_items'][0][]
  servicebarItems: ReturnType<typeof mapServicebar>['menu_items'][0][]
  legoId: boolean
  markets: MainTopbarRegionModalProps
  showBurgerButton?: boolean
  hideBottomBar?: boolean
  hideCTA?: boolean
  hideRightMenuItems?: boolean
}

export function MainTopbarMobileModal({
  legoId,
  leftMenuItems,
  rightMenuItems,
  servicebarItems,
  markets,
  showBurgerButton,
  hideBottomBar,
  hideCTA,
  hideRightMenuItems,
}: MainTopbarMobileModalProps) {
  const [show, setShow] = useState(false)
  const [top, setTop] = useState(0)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const toggle = useCallback(() => setShow((s) => !s), [])
  const isMobile = !useIsWindowBiggerThan(768)
  const t = useStaticText()

  useEffect(() => {
    setTop(buttonRef.current?.getBoundingClientRect().bottom ?? 0)
  }, [show])

  useDocumentScrollBlock(show)

  return (
    (showBurgerButton || isMobile) && (
      <>
        <SC.OverlayWrapper show={show} top={top}>
          <SC.OverlayScrollWrapper>
            {[...leftMenuItems, ...servicebarItems].map((menuItem, i) => {
              if (isButton(menuItem)) {
                return (
                  <a
                    className={itemText}
                    href={menuItem.button.link.href}
                    key={i}
                    onClick={() =>
                      matomo.trackEvent(
                        'engagement',
                        'all',
                        ['top menu'],
                        [menuItem.button.link.title],
                        'menu'
                      )
                    }
                  >
                    {menuItem.button.link.title}
                  </a>
                )
              }

              if (isCTA(menuItem)) {
                return (
                  <div key={menuItem.text}>
                    <CTA
                      className={itemText}
                      cta={menuItem}
                      onClick={() =>
                        matomo.trackEvent(
                          'engagement',
                          'all',
                          ['top menu'],
                          [menuItem?.text],
                          'menu'
                        )
                      }
                    />
                  </div>
                )
              }

              // if it's not a button or a CTA, it's a dropdown
              if (!('dropdown' in menuItem)) return null

              return (
                <MobileMenuAccordion
                  key={i}
                  links={menuItem.dropdown.links}
                  title={menuItem.dropdown.title}
                />
              )
            })}
            {rightMenuItems.map((menuItem, i) => {
              if (isButton(menuItem)) {
                return (
                  <a
                    className={itemText}
                    href={menuItem.button.link.href}
                    key={i}
                    onClick={() =>
                      matomo.trackEvent(
                        'engagement',
                        'all',
                        ['top menu'],
                        [menuItem.button.link.title],
                        'menu'
                      )
                    }
                  >
                    {menuItem.button.link.title}
                  </a>
                )
              }

              if (isCTA(menuItem)) {
                return (
                  <div key={menuItem.text}>
                    <CTA
                      className={clsx(
                        menuItem.type !== CTAType.PRIMARY ? itemText : null,
                        menuItem.type === CTAType.PRIMARY ? menuCTA.primary : null,
                        menuItem.type === CTAType.PRIMARY ? 'primaryCTA' : null,
                        !hideCTA && menuItem.type === CTAType.PRIMARY ? menuCTA.hidden : null,
                        !hideRightMenuItems && menuItem.type !== CTAType.PRIMARY
                          ? menuCTA.hidden
                          : null,
                        menuItem.type === CTAType.TERTIARY && menuItem.icon
                          ? menuCTA.linkWithIcon
                          : null
                      )}
                      colorTheme={ctaColorTheme(menuItem.type)}
                      cta={menuItem}
                      data-text={menuItem?.text}
                      onClick={() =>
                        matomo.trackEvent(
                          'engagement',
                          'all',
                          ['top menu'],
                          [menuItem?.text],
                          'menu'
                        )
                      }
                      prefix={menuItem.icon ? <SvgIcon name={menuItem.icon} size={18} /> : null}
                      type={menuItem.type}
                    />
                  </div>
                )
              }

              return (
                <MobileMenuAccordion
                  key={i}
                  links={menuItem.dropdown.links}
                  title={menuItem.dropdown.title}
                />
              )
            })}
          </SC.OverlayScrollWrapper>
          {hideBottomBar && (
            <SC.BottomBar>
              <MainTopbarRegionModal {...markets} />
              <MainTopbarAuth legoId={legoId} showOnTop={true} />
            </SC.BottomBar>
          )}
        </SC.OverlayWrapper>

        <SC.MobileOnlyButton aria-label={t.header.mobile_menu} onClick={toggle} ref={buttonRef}>
          <Icon icon={show ? ICONS.CROSS : ICONS.MENU} />
        </SC.MobileOnlyButton>
      </>
    )
  )
}
